import { useEffect, useRef, useCallback } from 'react';
import { X, Minus, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { QuickActions } from './QuickActions';
import type { ChatMessageData, SuggestedAction, ChatLabels } from './ChatWidget.types';

interface ChatWindowProps {
  messages: ChatMessageData[];
  isLoading?: boolean;
  suggestedActions?: SuggestedAction[];
  onSendMessage: (message: string) => void;
  onClose: () => void;
  onMinimize: () => void;
  labels: Required<ChatLabels>;
  locale?: string;
}

export function ChatWindow({
  messages,
  isLoading = false,
  suggestedActions = [],
  onSendMessage,
  onClose,
  onMinimize,
  labels,
  locale = 'en',
}: ChatWindowProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (messagesEndRef.current && typeof messagesEndRef.current.scrollIntoView === 'function') {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading]);

  // Focus management and Escape key
  useEffect(() => {
    previousActiveElement.current = document.activeElement as HTMLElement;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }

      // Focus trap
      if (e.key === 'Tab' && panelRef.current) {
        const focusable = panelRef.current.querySelectorAll<HTMLElement>(
          'button, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previousActiveElement.current?.focus();
    };
  }, [onClose]);

  const handleActionClick = useCallback(
    (message: string) => {
      onSendMessage(message);
    },
    [onSendMessage]
  );

  const showWelcome = messages.length === 0;

  return (
    <motion.div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-label={labels.title}
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.95 }}
      transition={{ type: 'spring', damping: 30, stiffness: 300 }}
      className="fixed bottom-20 right-4 z-40 flex w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900 sm:bottom-20 sm:right-6 sm:w-96 sm:max-h-[500px] max-h-[calc(100vh-6rem)] md:max-h-[500px]"
      data-testid="chat-window"
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 bg-blue-600 px-4 py-3 dark:border-gray-700 dark:bg-blue-700">
        <h2 className="text-sm font-semibold text-white">{labels.title}</h2>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onMinimize}
            className="rounded-lg p-1.5 text-blue-100 transition-colors hover:bg-blue-500 hover:text-white"
            aria-label={labels.close}
            data-testid="chat-minimize-button"
          >
            <Minus className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-blue-100 transition-colors hover:bg-blue-500 hover:text-white"
            aria-label={labels.close}
            data-testid="chat-close-button"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-[200px]">
        {showWelcome && (
          <div className="text-center">
            <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
              {labels.welcome}
            </p>
            {suggestedActions.length > 0 && (
              <QuickActions
                actions={suggestedActions}
                onActionClick={handleActionClick}
              />
            )}
          </div>
        )}

        {messages.map((msg) => (
          <ChatMessage key={msg.id} message={msg} locale={locale} />
        ))}

        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-gray-400 dark:text-gray-500" data-testid="chat-typing-indicator">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>{labels.typing}</span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <ChatInput
        onSend={onSendMessage}
        isLoading={isLoading}
        labels={{ placeholder: labels.placeholder, send: labels.send }}
      />
    </motion.div>
  );
}
